import React, { useState } from "react";
import {
  Autocomplete,
  Container,
  Typography,
  Box,
  IconButton,
  Divider,
  Fab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  CircularProgress,
  createFilterOptions,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AddIcon from "@mui/icons-material/Add";
import { useAcquisitions } from "src/hooks/acquisition-import/useAcquisitions";
import { useCreateAcquisition } from "src/hooks/acquisition-import/useCreateAcquisition";
import { useAcquisitionSubsidiaries } from "src/hooks/acquisition-import/useAcquisitionSubsidiaries";
import {
  Acquisition,
  AcquisitionSubsidiaryOption,
  displayAcquisitionRequestId,
  getAcquisitionImportStage,
} from "models/acquisition-import";
import { AcquisitionRecordRow } from "./AcquisitionRecordRow";

const subsidiaryFilter = createFilterOptions<AcquisitionSubsidiaryOption>({
  stringify: (option) =>
    [option.label, option.name, option.id].filter(Boolean).join(" "),
});

const AcquisitionImports: React.FC = () => {
  const navigate = useNavigate();
  const { acquisitions, loading } = useAcquisitions();
  const {
    createAcquisition,
    loading: creating,
    error,
    setError,
  } = useCreateAcquisition();
  const {
    items: subsidiaries,
    loading: loadingSubsidiaries,
    error: subsidiariesError,
    loadSubsidiaries,
  } = useAcquisitionSubsidiaries();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [name, setName] = useState("");
  const [subsidiary, setSubsidiary] =
    useState<AcquisitionSubsidiaryOption | null>(null);
  const [jiraTicket, setJiraTicket] = useState("");
  const [comment, setComment] = useState("");
  const [subsidiaryMessage, setSubsidiaryMessage] = useState("");

  const openDialog = () => {
    setError(null);
    setSubsidiaryMessage("");
    setDialogOpen(true);
    loadSubsidiaries();
  };

  const handleCreate = async () => {
    setSubsidiaryMessage("");
    if (!name.trim()) {
      return;
    }
    if (!subsidiary) {
      setSubsidiaryMessage("Select a subsidiary.");
      return;
    }
    const result = await createAcquisition({
      name: name.trim(),
      subsidiary: subsidiary.name,
      subsidiaryId: subsidiary.id,
      ...(jiraTicket.trim() ? { jiraTicket: jiraTicket.trim() } : {}),
      ...(comment.trim() ? { comment: comment.trim() } : {}),
    });
    if (result) {
      setDialogOpen(false);
      setName("");
      setSubsidiary(null);
      setJiraTicket("");
      setComment("");
      navigate(`/acquisition-imports/${result.acquisitionId}`);
    }
  };

  return (
    <Container sx={{ pt: 4, pb: 10 }}>
      <Box display="flex" alignItems="center" justifyContent="center" mb={3}>
        <IconButton
          onClick={() => navigate("/")}
          size="small"
          sx={{ alignSelf: "flex-start", flexGrow: 0 }}
        >
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h4" sx={{ flexGrow: 1, textAlign: "center" }}>
          Acquisition Import
        </Typography>
        <Box sx={{ flexGrow: 0, width: "48px" }} />
      </Box>

      <Divider sx={{ mb: 4 }} />

      <Typography variant="h5" gutterBottom>
        Acquisitions
      </Typography>

      {loading ? (
        <Typography>Loading...</Typography>
      ) : acquisitions.length === 0 ? (
        <Typography>No acquisitions yet. Add one to get started.</Typography>
      ) : (
        <Box>
          {acquisitions.map((item: Acquisition) => {
            const lastStage = item.lastStageCode
              ? getAcquisitionImportStage(item.lastStageCode)?.label ||
                item.lastStageCode
              : "";
            const summary = [
              lastStage ? `Last stage: ${lastStage}` : "",
              item.runCount ? `${item.runCount} run(s)` : "",
              item.createdByName || item.createdByEmail
                ? `Requestor: ${item.createdByName || item.createdByEmail}`
                : "",
            ]
              .filter(Boolean)
              .join(" - ");
            return (
              <AcquisitionRecordRow
                key={item.acquisitionId}
                requestId={displayAcquisitionRequestId(item.acquisitionId)}
                title={item.name}
                summary={summary}
                status={item.lastRunStatus}
                createdAt={item.lastRunAt || item.createdAt}
                onClick={() =>
                  navigate(`/acquisition-imports/${item.acquisitionId}`)
                }
              />
            );
          })}
        </Box>
      )}

      <Fab
        variant="extended"
        color="primary"
        onClick={openDialog}
        sx={{ position: "fixed", bottom: 24, right: 24 }}
      >
        <AddIcon sx={{ mr: 1 }} />
        Add Acquisition
      </Fab>

      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>New Acquisition</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Acquisition Name"
            fullWidth
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          <Autocomplete
            options={subsidiaries}
            value={subsidiary}
            onChange={(_event, value) => {
              setSubsidiary(value);
              if (value) {
                setSubsidiaryMessage("");
              }
            }}
            getOptionLabel={(option) => option.label || option.name}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            filterOptions={subsidiaryFilter}
            loading={loadingSubsidiaries}
            autoHighlight
            renderInput={(params) => (
              <TextField
                {...params}
                margin="dense"
                label="Subsidiary"
                required
                error={Boolean(subsidiaryMessage)}
                helperText={
                  subsidiaryMessage ||
                  (loadingSubsidiaries
                    ? "Loading subsidiaries..."
                    : "Type to filter, then pick a subsidiary")
                }
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loadingSubsidiaries ? (
                        <CircularProgress color="inherit" size={20} />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
          <TextField
            margin="dense"
            label="Jira Ticket"
            fullWidth
            value={jiraTicket}
            onChange={(e) => setJiraTicket(e.target.value)}
          />
          <TextField
            margin="dense"
            label="Comment"
            fullWidth
            multiline
            minRows={2}
            value={comment}
            onChange={(e) => setComment(e.target.value)}
          />
          {(error || subsidiariesError) && (
            <Typography variant="body2" color="error" sx={{ mt: 1 }}>
              {error || subsidiariesError}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleCreate}
            disabled={!name.trim() || creating || loadingSubsidiaries}
          >
            {creating ? <CircularProgress size={20} /> : "Create"}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default AcquisitionImports;
